// @ts-nocheck

import * as Sentry from '@sentry/react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import { Switch, Route } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import styled from 'styled-components';

import Client, { Target, fetchClientInfos } from '../../client';
import { REDIRECT_KOHLANTA_2023 } from '../../constants/storage';
import { NAVBAR_BASE_HEIGHT } from '../../constants/ui/navbar';
import { TABBAR_BASE_HEIGHT } from '../../constants/ui/tabbar';
import AppContextProvider from '../../contexts/AppContextProvider';
import CSSVariablesContextProvider from '../../contexts/CSSVariablesContextProvider';
import { getAppDOMElement, getPortalRootDOMElement } from '../../DOMElements';
import { useLocalStorage, usePrevious } from '../../hooks';
import { __ } from '../../i18n/l10n';
import { isAndroid, isEmbedded } from '../../lib/useragent';
import BoomWelcomeAnimationContainer from '../../modules/animations/containers/BoomWelcomeAnimationContainer';
import GiftRaceStartAnimationContainer from '../../modules/animations/containers/GiftRaceStartAnimationContainer';
import OverlayAnimationContainer from '../../modules/animations/containers/OverlayAnimationContainer';
import GridsRouteWatcherContainer from '../../modules/grids/containers/routes/GridsRouteWatcherContainer';
import { isGrids } from '../../modules/grids/utils/routes';
import { KLGlobalStatuses } from '../../modules/koh-lanta/types/global';
import RuntimeListener from '../../RuntimeListener';
import {
  AnimationsGlobalStyle,
  AppInnerStyle,
  ResetStyle,
  RootGlobalStyle,
  SportRadarGlobalStyle,
} from '../../styled-components/app';
import { ZIndices } from '../../styled-components/dark/common';
import GlobalStyleFonts from '../../styled-components/fonts';
import theme from '../../styled-components/theme';
import { RoutePatterns } from '../../types/routes';
import { scrollToTop, user as UserUtils } from '../../utils';
import WinaAppPlugin from '../../utils/plugins/WinaApp';
import isLineUp from '../../utils/routes/isLineUp';
import isMatch from '../../utils/routes/isMatch';
import isSports from '../../utils/routes/isSports';
import { saveUserhasConnected } from '../../utils/userStorage';
import AppNetworkStatusContainer from '../AppNetworkStatus/AppNetworkStatusContainer';
import DesktopLayoutContainer from '../DesktopLayout/DesktopLayoutContainer';
import DocumentTitle from '../DocumentTitle';
import FlashMessagesListContainer from '../FlashMessagesList/FlashMessagesListContainer';
import MobileLayoutContainer from '../MobileLayout/MobileLayoutContainer';
import PiPHandler from '../PiPHandler';
import PiPProviderContainer from '../PiPProvider/PiPProviderContainer';
import RealityCheckModalContainer from '../RealityCheckModal/RealityCheckModalContainer';
import ScrollTopFloatingButtonContainer from '../ScrollTopFloatingButton/ScrollTopFloatingButtonContainer';
import ToastrFactoryContainer from '../ToastrFactory/ToastrFactoryContainer';
import UserNotificationContainer from '../UserNotification/UserNotificationContainer';
import Viewport from '../Viewport';
import WinModalContainer from '../WinModal/WinModalContainer';

const handleAutoScrollTopOnPageChange = (pathname, previousPathname) => {
  if (
    isSports(pathname) ||
    (isMatch(pathname) && isLineUp(previousPathname)) ||
    (isMatch(previousPathname) && isLineUp(pathname))
  ) {
    return;
  }
  scrollToTop();
};

const StyledScrollTopFloatingButtonContainer = styled(ScrollTopFloatingButtonContainer)`
  position: ${Target.__MOBILE__ ? 'absolute' : 'fixed'};
  z-index: ${ZIndices.scrollTopFloatingButton};
  left: 50%;
  top: calc(calc(100% - ${TABBAR_BASE_HEIGHT}em - ${NAVBAR_BASE_HEIGHT}em) * 0.95);

  transform: translateX(-57%);

  &:active {
    transform: translateX(-57%) scale(0.9);
  }
`;

type AppProps = {
  setFlagNewUser: () => void;
  isLicenceES: boolean;
  tryLoginEmbedded: () => void;
  triggerDeprecatedPopup: (isLicenceES: boolean) => void;
  openKLView: () => void;
  isKLEnabled: boolean;
  klGlobalStatus: string;
  klHasUserNeverOptIn: boolean;
  didUserReceiveData: boolean;
  className?: string;
};

const App = ({
  setFlagNewUser,
  isLicenceES,
  tryLoginEmbedded,
  triggerDeprecatedPopup,
  didUserReceiveData,
  openKLView,
  isKLEnabled,
  klGlobalStatus,
  klHasUserNeverOptIn,
  className = '',
}: AppProps) => {
  const history = useHistory();
  const location = useLocation();

  const portalRootDOMElement = getPortalRootDOMElement();
  const appDOMElem = getAppDOMElement();

  const locationSearchRef = useRef(null);
  const previousPathname = usePrevious(location.pathname);
  const isSrPlayerWidgetOpen = location.hash.indexOf('#playerStats') === 0;
  const isBrWidgetOpen = location.hash.indexOf('#cupRoster') === 0 || location.hash.indexOf('#liveTable') === 0;
  const isGridRoute = isGrids(location.pathname);
  const [hasRedirect, setHasRedirect] = useLocalStorage(REDIRECT_KOHLANTA_2023, false);

  useEffect(() => {
    if (location.pathname !== previousPathname) {
      handleAutoScrollTopOnPageChange(location.pathname, previousPathname);
    }
  }, [location.pathname, previousPathname]);

  useEffect(() => {
    locationSearchRef.current = location.search;
  }, [location.search]);

  useEffect(() => {
    const connected = window.getOuid?.() > 0;

    if (connected) {
      saveUserhasConnected();
    }

    const { isConnectedAtLeastOneTime } = UserUtils;
    if (locationSearchRef.current.length > 0 && locationSearchRef.current.indexOf('lp=') > -1) {
      if (connected === false || isConnectedAtLeastOneTime() === false) {
        // Flag a user as a new one and keep it for the whole session
        setFlagNewUser();
      }
    }
  }, [setFlagNewUser]);

  useEffect(() => {
    // On va fetch les infos clients du plugin natif
    fetchClientInfos(WinaAppPlugin);
    if (Target.__MOBILE__ && Client.embed) {
      // Pour signaler a l'appli que l'on est bien la
      window.location.href = 'wam://betting';
      // On essaie de s'authentifier silencieusement (si on vient du poker) et qu'on est pas deja connecté
      if (!window.PRELOADED_STATE?.cc?.user?.nickname) {
        tryLoginEmbedded();
      }
    }
  }, [tryLoginEmbedded]);

  // Trigger deprecation error on Android with plugin auth v2 not available
  useEffect(() => {
    if (isAndroid && isEmbedded) {
      const isAvailable = !!window.Auth || !!window.AuthBetting;

      if (!isAvailable) {
        triggerDeprecatedPopup(isLicenceES);
      }
    }
  }, [isLicenceES, triggerDeprecatedPopup]);

  useEffect(() => {
    if (isSrPlayerWidgetOpen && !location.state?.playerWidgetProps) {
      history.goBackOrReplace(location.pathname);
    }

    if (isBrWidgetOpen && !location.state?.brWidgetProps) {
      history.goBackOrReplace(location.pathname);
    }
  }, [history, isSrPlayerWidgetOpen, isBrWidgetOpen, location]);

  useEffect(() => {
    if (isGridRoute) {
      document.body.classList.add('grids');
    } else {
      document.body.classList.remove('grids');
    }
    return () => {
      if (!isGridRoute) {
        document.body.classList.remove('grids');
      }
    };
  }, [isGridRoute]);

  useEffect(() => {
    if (
      isKLEnabled &&
      klGlobalStatus === KLGlobalStatuses.RUNNING &&
      Target.__MOBILE__ &&
      didUserReceiveData &&
      klHasUserNeverOptIn &&
      !hasRedirect
    ) {
      setHasRedirect(true);
      openKLView();
    }
  }, [didUserReceiveData, hasRedirect, setHasRedirect, openKLView, isKLEnabled, klGlobalStatus, klHasUserNeverOptIn]);

  return (
    <ThemeProvider theme={theme}>
      <CSSVariablesContextProvider>
        <GlobalStyleFonts />
        <RootGlobalStyle />
        <SportRadarGlobalStyle />
        <ResetStyle />
        <AnimationsGlobalStyle />
        <AppInnerStyle className={className}>
          <Viewport>
            <Sentry.ErrorBoundary fallback={'An error has occurred'}>
              <DocumentTitle title={__('!desc:DocumentTitle')}>
                <PiPProviderContainer>
                  <AppContextProvider>
                    <RuntimeListener>
                      <UserNotificationContainer />
                      {Target.__MOBILE__ ? <MobileLayoutContainer /> : <DesktopLayoutContainer />}
                      {createPortal(
                        Target.__MOBILE__ ? <ToastrFactoryContainer /> : <FlashMessagesListContainer />,
                        portalRootDOMElement,
                      )}
                      {(isLicenceES || Target.__STANDALONE__) &&
                        createPortal(<AppNetworkStatusContainer />, portalRootDOMElement)}
                      <Switch>
                        <Route path={RoutePatterns.GRIDS} component={GridsRouteWatcherContainer} />
                      </Switch>
                      {createPortal(<WinModalContainer hasNavBarSpacing={Target.__MOBILE__} />, portalRootDOMElement)}
                      {createPortal(<PiPHandler />, appDOMElem)}
                      {createPortal(<StyledScrollTopFloatingButtonContainer />, portalRootDOMElement)}
                      {createPortal(<OverlayAnimationContainer />, portalRootDOMElement)}
                      {createPortal(<BoomWelcomeAnimationContainer />, portalRootDOMElement)}
                      {createPortal(<GiftRaceStartAnimationContainer />, portalRootDOMElement)}
                      <RealityCheckModalContainer />
                    </RuntimeListener>
                  </AppContextProvider>
                </PiPProviderContainer>
              </DocumentTitle>
            </Sentry.ErrorBoundary>
          </Viewport>
        </AppInnerStyle>
      </CSSVariablesContextProvider>
    </ThemeProvider>
  );
};

export default App;
